/**
 * Generic destructible prop data — phone boxes, bins, crates, etc.
 * Pure; no Phaser imports.
 */

export type PropKind = 'phone-box' | 'bin' | 'crate' | 'stall';
export type PropDropKind = 'weapon' | 'food' | 'none';

export interface PropDef {
  readonly id: string;
  readonly label: string;
  readonly kind: PropKind;
  /** Optional sprite atlas assetId for rendered prop art. */
  readonly assetId?: string;
  /** Hits required to destroy. */
  readonly health: number;
  /** Placeholder fill colour. */
  readonly color: number;
  readonly dropKind: PropDropKind;
  /** Weapon def id to drop (only when dropKind === 'weapon'). */
  readonly dropWeaponId?: string;
  /** HP amount to restore (only when dropKind === 'food'). */
  readonly dropHeal?: number;
}

/** Placement of a destructible prop within a stage level. */
export interface PropPlacement {
  /** References a PropDef id in PROPS. */
  readonly propId: string;
  /** Horizontal position as a fraction of levelWidth (0–1). */
  readonly xFrac: number;
}

/** A standalone health pickup placed in the world — heals the nearest player on contact. */
export interface HealthPickupPlacement {
  /** Horizontal position as a fraction of levelWidth (0–1). */
  readonly xFrac: number;
  /** HP amount to restore on pickup. */
  readonly heal: number;
}

export const PROPS: readonly PropDef[] = [
  {
    id: 'soho-bin',
    label: 'BIN',
    kind: 'bin',
    health: 2,
    color: 0x2d5a2d,
    dropKind: 'food',
    dropHeal: 12,
  },
  {
    id: 'soho-phone-box',
    label: 'PHONE BOX',
    kind: 'phone-box',
    health: 4,
    color: 0xd4001e,
    dropKind: 'none',
  },
  {
    id: 'docks-crate',
    label: 'CRATE',
    kind: 'crate',
    health: 3,
    color: 0x8b6914,
    dropKind: 'weapon',
    dropWeaponId: 'pipe',
  },
  {
    id: 'docks-barrel',
    label: 'BARREL',
    kind: 'crate',
    health: 2,
    color: 0x5c3a1a,
    dropKind: 'food',
    dropHeal: 8,
  },
  // Stage 4 — Northern Line
  {
    id: 'tube-bin',
    label: 'TUBE BIN',
    kind: 'bin',
    health: 2,
    color: 0x4a4a4a,
    dropKind: 'food',
    dropHeal: 10,
  },
  {
    id: 'tube-locker',
    label: 'MAINTENANCE LOCKER',
    kind: 'crate',
    health: 3,
    color: 0x7a8a5a,
    dropKind: 'weapon',
    dropWeaponId: 'bat',
  },
  // Stage 5 — Croydon Spice Lab
  {
    id: 'croydon-chem-barrel',
    label: 'CHEM BARREL',
    kind: 'crate',
    health: 2,
    color: 0x2d6b2d,
    dropKind: 'food',
    dropHeal: 15,
  },
  {
    id: 'croydon-gas-canister',
    label: 'GAS CANISTER',
    kind: 'crate',
    health: 3,
    color: 0xb5b5b5,
    dropKind: 'weapon',
    dropWeaponId: 'pipe',
  },
  // Stage 6 — Bethnal Green
  {
    id: 'bethnal-bin',
    label: 'BIN',
    kind: 'bin',
    health: 2,
    color: 0x333333,
    dropKind: 'food',
    dropHeal: 10,
  },
  {
    id: 'bethnal-placard',
    label: 'PROTEST SIGN',
    kind: 'crate',
    health: 2,
    color: 0xcc2200,
    dropKind: 'weapon',
    dropWeaponId: 'bat',
  },
  // Stage 8 — Canary Wharf
  {
    id: 'canary-planter',
    label: 'GLASS PLANTER',
    kind: 'crate',
    health: 3,
    color: 0x2d4f2d,
    dropKind: 'food',
    dropHeal: 8,
  },
  {
    id: 'canary-glass-barrier',
    label: 'GLASS BARRIER',
    kind: 'crate',
    health: 4,
    color: 0x8fc8e0,
    dropKind: 'weapon',
    dropWeaponId: 'bat',
  },
  // Stage 9 — Westminster
  {
    id: 'westminster-barrier',
    label: 'CROWD BARRIER',
    kind: 'crate',
    health: 4,
    color: 0x4a5a6a,
    dropKind: 'weapon',
    dropWeaponId: 'pipe',
  },
  {
    id: 'westminster-bin',
    label: 'STREET BIN',
    kind: 'bin',
    health: 2,
    color: 0x2d2d2d,
    dropKind: 'food',
    dropHeal: 10,
  },
];

/** Look up a prop definition by id. Returns undefined if not found. */
export function getPropDef(id: string): PropDef | undefined {
  return PROPS.find((p) => p.id === id);
}

/** Visual hit-box dimensions for each prop kind (width × height in pixels). */
export const PROP_DIMENSIONS: Record<PropKind, { w: number; h: number }> = {
  'phone-box': { w: 32, h: 48 },
  bin: { w: 24, h: 32 },
  crate: { w: 40, h: 32 },
  stall: { w: 48, h: 36 },
};

// ---------------------------------------------------------------------------
// Stage hazard zones — permanent environmental hazards (third rail, acid vat,
// etc.) that deal bonus damage to a thrown entity on contact.
// ---------------------------------------------------------------------------

export interface HazardZoneDef {
  readonly id: string;
  readonly label: string;
  /** Bonus damage dealt to a thrown entity that enters this zone. */
  readonly throwBonusDamage: number;
  /**
   * When true, any entity thrown into this zone is instantly KO'd regardless
   * of remaining HP (canal pits, chasms — Final Fight / TMNT style).
   */
  readonly instantKO?: boolean;
}

/** Placement of a hazard zone within a stage level. */
export interface HazardZonePlacement {
  /** References a HazardZoneDef id in HAZARD_ZONES. */
  readonly hazardId: string;
  /** Horizontal centre position as a fraction of levelWidth (0–1). */
  readonly xFrac: number;
  /** Zone width as a fraction of levelWidth (0–1). */
  readonly wFrac: number;
}

export const HAZARD_ZONES: readonly HazardZoneDef[] = [
  {
    id: 's1-canal',
    label: 'CANAL',
    throwBonusDamage: 999,
    instantKO: true,
  },
  {
    id: 's4-third-rail',
    label: 'THIRD RAIL',
    throwBonusDamage: 30,
  },
  {
    id: 's5-acid-vat',
    label: 'ACID VAT',
    throwBonusDamage: 35,
  },
];

/** Look up a hazard zone definition by id. Returns undefined if not found. */
export function getHazardZoneDef(id: string): HazardZoneDef | undefined {
  return HAZARD_ZONES.find((h) => h.id === id);
}
